import { useState, useEffect } from "react";
import { Button } from "@/projects/Civi_Circle_Admin/components/ui/button";
import { Input } from "@/projects/Civi_Circle_Admin/components/ui/input";
import { Label } from "@/projects/Civi_Circle_Admin/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/projects/Civi_Circle_Admin/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/Civi_Circle_Admin/components/ui/tabs";
import { useToast } from "@/projects/Civi_Circle_Admin/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/context/useUser";
import { getTenantId, setLocalUserInfo } from "@/lib/storeManager";
import MMNavigationRoutes from "../utils/navigationRoutes";
import { ZulipService } from "@/services/APIs";
import { validatePassword } from "@/utils/passwordValidation";

export default function Auth() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [organization, setOrganization] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const { toast } = useToast();
  const { login, isAuthenticated } = useUser();
  const navigate = useNavigate();
  const tenantId = getTenantId() ?? "";

  const fetchTokenOnce = async () => {
    const existingToken = localStorage.getItem("zulipToken");
    const existingUser = localStorage.getItem("zulipUserId");

    if (existingToken && existingUser) {
      return;
    } // Already fetched

    try {
      const res = await ZulipService.getZulipToken();
      localStorage.setItem("zulipToken", res.basic_token);
      localStorage.setItem("zulipUserId", res.zulip_user_id);
    } catch (err) {
      console.error("Zulip Token fetch failed:", err);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    // Form validation
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Please fill in all required fields correctly.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      console.log("Form values:", { email, password });
      console.log("Tenant ID:", tenantId);

      const response = await login(email, password);
      if (response) {
        toast({
          title: "Success",
          description: "Login successful!",
        });
        await fetchTokenOnce(); // fetch Zulip token after login
        navigate("/mm" + MMNavigationRoutes.DASHBOARD);
      } else {
        toast({
          title: "Error",
          description: "Login failed. Please check your credentials.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Login failed:", error);
      toast({
        title: "Error",
        description: "Login failed. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !fullName || !organization) {
      toast({
        title: "Error",
        description: "Please fill in all required fields correctly.",
        variant: "destructive",
      });
      return;
    }

    // Parse full name to extract first and last name
    const nameParts = fullName.trim().split(/\s+/);
    const firstName = nameParts[0] || "";
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

    // Validate password using comprehensive validation
    const validationResult = validatePassword(password, {
      email: email,
      firstName: firstName,
      lastName: lastName,
    });

    if (!validationResult.isValid) {
      setPasswordErrors(validationResult.errors);
      toast({
        title: "Password Validation Failed",
        description: "Please check the password requirements below.",
        variant: "destructive",
      });
      return;
    }

    // Clear errors if validation passes
    setPasswordErrors([]);

    setLoading(true);
    try {
      const values = {
        work_email: email,
        full_name: fullName,
        organization: organization,
        password: password,
      };

      console.log("Form values:", values);
      console.log("Tenant ID:", tenantId);

      // Get API base URL from environment variable
      const apiBaseUrl =
        import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";
      const apiUrl = `${apiBaseUrl}/city_collab/organization-sign-up/`;

      console.log("API URL:", apiUrl);

      // Call the API with tenant ID in header
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-ID": tenantId || "scane hall project",
        },
        body: JSON.stringify(values),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Sign up successfully:", result);

        // Extract organization ID from the response
        const organizationId = result.organization?.id;
        console.log("Organization ID:", organizationId);
        localStorage.setItem("accessToken", result.access);
        localStorage.setItem("refreshToken", result.refresh);

        // Store user info so header shows full name (API returns full_name; first_name/last_name may be empty)
        const apiUser = result.user ?? {};
        const fullNameFromApi = (
          apiUser.full_name ??
          values.full_name ??
          ""
        ).trim();
        const nameParts = fullNameFromApi ? fullNameFromApi.split(/\s+/) : [];
        const firstName = apiUser.first_name ?? nameParts[0] ?? "";
        const lastName =
          apiUser.last_name ??
          (nameParts.length > 1 ? nameParts.slice(1).join(" ") : "");
        setLocalUserInfo({
          first_name: firstName,
          last_name: lastName,
          full_name: fullNameFromApi ?? undefined,
          email: apiUser.email ?? values.work_email,
          user_type: apiUser.user_type,
        });

        toast({
          title: "Success",
          description: "Account created successfully!",
        });
        await fetchTokenOnce(); // fetch Zulip token after signup
        // Redirect to dashboard after successful signup
        navigate(
          "/mm" +
            MMNavigationRoutes.EDIT_CHAPTER_PROFILE(organizationId) +
            `?email=${encodeURIComponent(values.work_email)}`
        );
      } else {
        const errorData = await response.json();
        console.error("API Error:", errorData);

        // Handle specific error cases
        if (errorData.details?.name) {
          toast({
            title: "Error",
            description:
              "Organization already exists. Please try with a different organization name.",
            variant: "destructive",
          });
        } else if (errorData.details?.full_name) {
          toast({
            title: "Error",
            description:
              "User already exists. Please try with a different email address.",
            variant: "destructive",
          });
        } else if (
          errorData.details &&
          typeof errorData.details === "string" &&
          errorData.details.includes("user_already_exists")
        ) {
          toast({
            title: "Error",
            description:
              "User already exists. Please try with a different email address.",
            variant: "destructive",
          });
        } else if (
          errorData.details &&
          typeof errorData.details === "string" &&
          errorData.details.includes("organization_exists")
        ) {
          toast({
            title: "Error",
            description:
              "Organization already exists. Please try with a different organization name.",
            variant: "destructive",
          });
        } else if (errorData.error) {
          toast({
            title: "Error",
            description: errorData.error,
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: "Registration failed. Please try again.",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error("Sign up failed:", error);
      toast({
        title: "Error",
        description: "Failed to create account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const zulipToken = localStorage.getItem("zulipToken");
    const zulipUserId = localStorage.getItem("zulipUserId");

    if (zulipToken && zulipUserId && isAuthenticated) {
      navigate("/mm" + MMNavigationRoutes.DASHBOARD);
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-2 sm:p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="px-4 text-center sm:px-6">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary sm:mb-4 sm:h-12 sm:w-12">
            <span className="text-base font-bold text-primary-foreground sm:text-lg">
              CC
            </span>
          </div>
          <CardTitle className="text-xl sm:text-2xl">Civi Circle</CardTitle>
          <CardDescription className="text-sm">
            Welcome to Civi Circle
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid h-9 w-full grid-cols-2 sm:h-10">
              <TabsTrigger value="signin" className="text-sm">
                Sign In
              </TabsTrigger>
              <TabsTrigger value="signup" className="text-sm">
                Sign Up
              </TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-3 sm:space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email" className="text-sm">
                    Email
                  </Label>
                  <Input
                    id="signin-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signin-password" className="text-sm">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="signin-password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      className="pr-10 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground sm:right-3"
                    >
                      {showPassword ? (
                        <EyeOff className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      ) : (
                        <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="h-9 w-full text-sm sm:h-10 sm:text-base"
                  disabled={loading}
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-3 sm:space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-email" className="text-sm">
                    Work Email
                  </Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-name" className="text-sm">
                    Full Name
                  </Label>
                  <Input
                    id="signup-name"
                    type="text"
                    placeholder="John Doe"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    required
                    className="text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-organization" className="text-sm">
                    Organization / Agency
                  </Label>
                  <Input
                    id="signup-organization"
                    type="text"
                    placeholder="Organization / Agency"
                    value={organization}
                    onChange={e => setOrganization(e.target.value)}
                    required
                    className="text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password" className="text-sm">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      type={showSignUpPassword ? "text" : "password"}
                      placeholder="Password (8-10 characters)"
                      value={password}
                      onChange={e => {
                        setPassword(e.target.value);
                        // Clear errors when user starts typing
                        if (passwordErrors.length > 0) {
                          setPasswordErrors([]);
                        }
                      }}
                      required
                      className="pr-10 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignUpPassword(!showSignUpPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground sm:right-3"
                    >
                      {showSignUpPassword ? (
                        <EyeOff className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      ) : (
                        <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="h-9 w-full text-sm sm:h-10 sm:text-base"
                  disabled={loading}
                >
                  {loading ? "Creating Account..." : "Start Free Trial"}
                </Button>

                {/* Password Validation Errors */}
                {passwordErrors.length > 0 && (
                  <div className="mt-4 rounded-md border border-destructive/50 bg-destructive/10 p-4">
                    <h4 className="mb-2 text-sm font-semibold text-destructive">
                      Password Requirements:
                    </h4>
                    <ul className="list-inside list-disc space-y-1 text-xs text-destructive">
                      {passwordErrors.map(error => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
